import type {
  CmmdDeviceRegistryDevice,
  CmmdDeviceRegistryRuntime,
  CmmdRuntimeInventoryRuntime,
} from "@cmmd-forge/contracts";
import {
  projectRuntimeInventoryToSidebarProjects,
  projectRuntimeInventoryToSidebarThreads,
} from "../components/sidebar/cmmdRuntimeInventoryProjection";
import { getStoredLocalDeviceRuntimeId } from "../deviceRegistryIdentity";
import { useCmmdRuntimeInventorySnapshot } from "./cmmdRuntimeInventoryResource";

export function useCmmdRuntimeInventory(input: {
  readonly registeredRuntimes: readonly CmmdDeviceRegistryRuntime[];
  readonly registeredDevices: readonly CmmdDeviceRegistryDevice[];
}) {
  const snapshot = useCmmdRuntimeInventorySnapshot();
  const authorizedRegisteredRuntimes = filterAuthorizedRegisteredRuntimes(input);
  const currentRegisteredRuntimeId = resolveConfirmedRegisteredRuntimeId(
    getStoredLocalDeviceRuntimeId(),
    authorizedRegisteredRuntimes,
  );
  const runtimes = filterRuntimeInventoryByRegisteredRuntime(
    snapshot.inventory?.runtimes ?? [],
    authorizedRegisteredRuntimes,
  );
  return {
    currentRegisteredRuntimeId,
    runtimes,
    sidebarProjects: projectRuntimeInventoryToSidebarProjects(runtimes, currentRegisteredRuntimeId),
    sidebarThreads: projectRuntimeInventoryToSidebarThreads(runtimes, currentRegisteredRuntimeId),
    threadDispositions: projectRuntimeInventoryToSidebarThreads(runtimes, null),
    isLoading: snapshot.isLoading,
    hasLoaded: snapshot.inventory !== null,
    error: snapshot.error,
  };
}

export function filterAuthorizedRegisteredRuntimes(input: {
  readonly registeredRuntimes: readonly CmmdDeviceRegistryRuntime[];
  readonly registeredDevices: readonly CmmdDeviceRegistryDevice[];
}): readonly CmmdDeviceRegistryRuntime[] {
  const deviceById = new Map(input.registeredDevices.map((device) => [device.id, device]));
  return input.registeredRuntimes.filter((runtime) => {
    if (runtime.status === "revoked") return false;
    if (runtime.deviceId === null) return true;
    const device = deviceById.get(runtime.deviceId);
    return Boolean(
      device && device.trustState !== "revoked" && device.authorization.revoked !== true,
    );
  });
}

export function filterRuntimeInventoryByRegisteredRuntime(
  inventory: readonly CmmdRuntimeInventoryRuntime[],
  registeredRuntimes: readonly CmmdDeviceRegistryRuntime[],
): readonly CmmdRuntimeInventoryRuntime[] {
  const registeredRuntimeIds = new Set(registeredRuntimes.map((runtime) => runtime.id));
  return inventory.filter(
    (entry) =>
      entry.runtime.runtimeKind === "cloud-vm" || registeredRuntimeIds.has(entry.runtime.id),
  );
}

export function resolveConfirmedRegisteredRuntimeId(
  storedRuntimeId: string | null,
  registeredRuntimes: readonly CmmdDeviceRegistryRuntime[],
): string | null {
  if (!storedRuntimeId) return null;
  return registeredRuntimes.some(
    (runtime) => runtime.id === storedRuntimeId && runtime.runtimeKind === "desktop-local",
  )
    ? storedRuntimeId
    : null;
}
